import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { apiClient } from '../api/apiClient'
import type { YardSearchData } from '../search/yardSearch'

/**
 * Assembles the cached yard snapshot for header search (ADR-002/ADR-007):
 *   cars   — GET /api/v1/yards/{fid}/assets  (kinematics flattened)
 *   tracks — GET /api/v1/tracks/routable?facilityId=
 *   jobs   — GET /api/v1/workorders?facilityId=
 *
 * Scale note: the assets endpoint returns every car on the facility's
 * tracks in one shot — bounded by yard size (hundreds–low thousands of
 * cars, one row per car). This is accepted for yard scale; if a facility
 * ever carries >10k live assets, add a server-side search endpoint
 * (see plan docs) rather than paginating here.
 *
 * Tenant: every key includes facilityId — switching facility can never
 * render the previous facility's results (cache misses re-fetch).
 * apiClient injects X-Facility-ID solely from nyos_active_scope (ADR-001),
 * which is the same source ScopeSwitcher writes — no JWT mismatch path.
 */

export interface YardAssetDTO {
  id: string
  reportingMark: string
  assetType?: string
  state?: string
  kinematics?: {
    position?: { longitude: number; latitude: number }
    currentTrackId?: string
    linearOffset?: number
  }
}

interface RoutableTrackDTO {
  id: string
  name: string
  trackType?: string
}

interface WorkOrderDTO {
  id: string
  woNumber?: string
  swlNumber?: string
  status?: string
}

export function useYardSearch(facilityId: string | undefined) {
  const cars = useQuery({
    queryKey: ['yard-search-cars', facilityId],
    queryFn: () => apiClient.get<YardAssetDTO[]>(`/api/v1/yards/${facilityId}/assets`),
    enabled: !!facilityId,
    staleTime: 30_000,
  })

  const tracks = useQuery({
    queryKey: ['yard-search-tracks', facilityId],
    queryFn: () =>
      apiClient.get<RoutableTrackDTO[]>(`/api/v1/tracks/routable?facilityId=${facilityId}`),
    enabled: !!facilityId,
    staleTime: 60_000,
  })

  const jobs = useQuery({
    queryKey: ['yard-search-jobs', facilityId],
    queryFn: () =>
      apiClient.get<WorkOrderDTO[]>(`/api/v1/workorders?facilityId=${facilityId}`),
    enabled: !!facilityId,
    staleTime: 30_000,
  })

  const data: YardSearchData = useMemo(
    () => ({
      cars: (cars.data ?? []).map((a) => ({
        id: a.id,
        reportingMark: a.reportingMark,
        trackId: a.kinematics?.currentTrackId,
        linearOffset: a.kinematics?.linearOffset,
        state: a.state,
      })),
      tracks: (tracks.data ?? []).map((t) => ({
        id: t.id,
        name: t.name,
        trackType: t.trackType,
      })),
      jobs: jobs.data ?? [],
    }),
    [cars.data, tracks.data, jobs.data]
  )

  return {
    data,
    isLoading: cars.isLoading || tracks.isLoading || jobs.isLoading,
    isError: cars.isError || tracks.isError || jobs.isError,
    /** Subset for external invalidation on WS events (Step 2 wiring). */
    queryKeys: {
      cars: ['yard-search-cars', facilityId] as const,
      tracks: ['yard-search-tracks', facilityId] as const,
      jobs: ['yard-search-jobs', facilityId] as const,
    },
  }
}
